import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { Icon } from './Icon'
import { useT } from '@/shared/i18n'

// ---------------------------------------------------------------------------
// Toast / ToastProvider
//
// Presentational toast notification system. Pure React/state — no global
// window.toast, no fiber introspection, no vanilla innerHTML (D8).
//
// Rendering: ToastProvider mounts a portal into #mbn-toast-host (a fixed
// element anchored at the bottom-center of the viewport). Each toast uses
// the .mbn-toast* classes from shared/styles/components.css.
//
// Usage:
//   // Wrap your app or surface shell:
//   <ToastProvider> ... </ToastProvider>
//
//   // From any descendant:
//   const { show } = useToast()
//   show('Record saved', { tone: 'ok' })
//   show('Error', { tone: 'danger', sub: 'Retry in a moment', duration: 5000 })
//
// API (contracts/component-library.md):
//   show(message, { tone?, sub?, duration? })
//   tone: 'ok' | 'info' | 'warn' | 'danger'  (default: 'ok')
//   sub: secondary line of text
//   duration: auto-dismiss delay in ms (default: 3500)
// ---------------------------------------------------------------------------

export type ToastTone = 'ok' | 'info' | 'warn' | 'danger'

export interface ToastOptions {
  tone?: ToastTone
  sub?: string
  duration?: number
}

export interface ToastEntry {
  id: string
  message: string
  tone: ToastTone
  sub?: string
}

interface ToastContextValue {
  show: (message: string, opts?: ToastOptions) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const DEFAULT_DURATION = 3500

// Monotonically-increasing id counter. Module-level so ids are unique across
// multiple mounts in the same document (rare in prod; matters in tests).
let _idSeq = 0
function nextId(): string {
  _idSeq += 1
  return `mbn-toast-${_idSeq}`
}

// Icon name per tone — uses the Icon registry (english names, currentColor).
const TONE_ICON: Record<ToastTone, Parameters<typeof Icon>[0]['name']> = {
  ok: 'check',
  info: 'info',
  warn: 'warn',
  danger: 'x',
}

// Whether motion should be suppressed. Honors BOTH the explicit user toggle
// (data-motion="reduced" on <html>, set by useDisplayPreferences) AND the OS
// "prefers-reduced-motion" setting — the CSS layer only reacts to the former,
// so the entrance animation, which is JS-driven here, must check the latter
// itself. matchMedia is guarded because jsdom (and very old browsers) may not
// provide it.
function prefersReducedMotion(): boolean {
  if (typeof document !== 'undefined') {
    if (document.documentElement.getAttribute('data-motion') === 'reduced') {
      return true
    }
  }
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// ToastItem — renders a single toast entry.
// ---------------------------------------------------------------------------

interface ToastItemProps {
  entry: ToastEntry
  onDismiss: (id: string) => void
}

function ToastItem({ entry, onDismiss }: ToastItemProps) {
  const t = useT()
  // When motion is reduced, render settled immediately (no opacity fade);
  // otherwise mount hidden and flip on the next frame to drive the CSS
  // transition.
  const reduceMotion = prefersReducedMotion()
  const [visible, setVisible] = useState(reduceMotion)

  // Trigger the CSS transition one tick after mount (opacity 0 → 1).
  // Skipped entirely under reduced motion.
  useEffect(() => {
    if (reduceMotion) return
    const raf = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(raf)
  }, [reduceMotion])

  const handleDismiss = useCallback(() => {
    onDismiss(entry.id)
  }, [entry.id, onDismiss])

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={clsx('mbn-toast', visible && 'is-in')}
    >
      <span
        className={clsx('mbn-toast__ic', `mbn-toast__ic--${entry.tone}`)}
        aria-hidden="true"
      >
        <Icon name={TONE_ICON[entry.tone]} size={14} />
      </span>
      <span className="mbn-toast__tx">
        <b>{entry.message}</b>
        {entry.sub && <span className="mbn-toast__sub">{entry.sub}</span>}
      </span>
      <button
        type="button"
        className="mbn-toast__x"
        aria-label={t('chrome.toast.dismiss')}
        onClick={handleDismiss}
      >
        ×
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ToastProvider — manages state, renders portal.
// ---------------------------------------------------------------------------

export interface ToastProviderProps {
  children: ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const [hostEl, setHostEl] = useState<HTMLElement | null>(null)

  // Ensure the portal host element exists in the document.
  useEffect(() => {
    let el = document.getElementById('mbn-toast-host')
    if (!el) {
      el = document.createElement('div')
      el.id = 'mbn-toast-host'
      document.body.appendChild(el)
    }
    setHostEl(el)
    return () => {
      // Only remove the host if we created it and there are no other providers.
      // In tests cleanup() handles the DOM; in prod this provider is long-lived.
    }
  }, [])

  const dismiss = useCallback((id: string) => {
    const handle = timers.current.get(id)
    if (handle !== undefined) {
      clearTimeout(handle)
      timers.current.delete(id)
    }
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const show = useCallback(
    (message: string, opts: ToastOptions = {}) => {
      const { tone = 'ok', sub, duration = DEFAULT_DURATION } = opts
      const id = nextId()
      const entry: ToastEntry = { id, message, tone, sub }

      setToasts((prev) => [...prev, entry])

      const handle = setTimeout(() => {
        timers.current.delete(id)
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, duration)
      timers.current.set(id, handle)
    },
    [],
  )

  // Clean up all pending timers when the provider unmounts.
  useEffect(() => {
    return () => {
      for (const handle of timers.current.values()) {
        clearTimeout(handle)
      }
      timers.current.clear()
    }
  }, [])

  const portal =
    hostEl !== null && toasts.length > 0
      ? createPortal(
          toasts.map((entry) => (
            <ToastItem key={entry.id} entry={entry} onDismiss={dismiss} />
          )),
          hostEl,
        )
      : null

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {portal}
    </ToastContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// useToast — consume the nearest ToastProvider's show() function.
// ---------------------------------------------------------------------------

/**
 * Returns a `show()` function to imperatively display a toast notification.
 * Must be called inside a `<ToastProvider>` subtree.
 *
 * @example
 *   const { show } = useToast()
 *   show('Saved', { tone: 'ok' })
 *   show('Error', { tone: 'danger', sub: 'Please try again' })
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (ctx === null) {
    throw new Error('useToast must be used inside a <ToastProvider>')
  }
  return ctx
}
